// Часы текущего календарного месяца в списке активных (RD-153).
// Источник — worked_hours (number|null) и hours_norm (integer|null) строки GET /api/user/ по контракту RD-154.
// Для разработки можно явно включить синтетический мок часов: VITE_EMPLOYEES_HOURS_MOCK=true в dev-режиме.
// Мок не подставляется автоматически, не маскирует ошибки API и не затрагивает нарушения.

export interface EmployeeHours {
    worked: number | null
    norm: number | null
}

export interface HoursBar {
    percent: number
    isLow: boolean
}

const LOW_RATIO = 0.6

const MONTHS = [
    'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
    'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'
]

export const isHoursMockEnabled = (): boolean =>
    import.meta.env.DEV && import.meta.env.VITE_EMPLOYEES_HOURS_MOCK === 'true'

// Название месяца из month_schedule.month (YYYY-MM, локальный месяц подразделения).
// Неизвестный формат — null: подпись месяца не выдумывается.
export const monthNameFromSchedule = (month: unknown): string | null => {
    if (typeof month !== 'string') return null
    const match = /^\d{4}-(\d{2})$/.exec(month)
    const index = match ? Number(match[1]) - 1 : -1
    return MONTHS[index] ?? null
}

const toHoursValue = (value: unknown): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null

// Синтетические стабильные значения: норма, < 60 %, превышение, ноль, неизвестные, дробные, нулевая норма.
const MOCK_HOURS: EmployeeHours[] = [
    { worked: 142, norm: 165 },
    { worked: 88, norm: 165 },
    { worked: 190, norm: 165 },
    { worked: 0, norm: 165 },
    { worked: null, norm: 165 },
    { worked: 120, norm: null },
    { worked: null, norm: null },
    { worked: 96.5, norm: 165.25 },
    { worked: 12, norm: 0 }
]

const stableIndex = (id: string, size: number): number => {
    let hash = 0
    for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0
    return hash % size
}

export const readEmployeeHours = (employee: { id: string, worked_hours?: unknown, hours_norm?: unknown }): EmployeeHours => {
    if (isHoursMockEnabled()) return MOCK_HOURS[stableIndex(String(employee.id), MOCK_HOURS.length)]
    return {
        worked: toHoursValue(employee.worked_hours),
        norm: toHoursValue(employee.hours_norm)
    }
}

// До двух знаков после запятой, без лишних нулей: 96, 96,5, 165,25.
export const formatHours = (value: number | null): string =>
    value === null ? '—' : value.toLocaleString('ru-RU', { maximumFractionDigits: 2 })

// Полоса только при известных факте и норме; при норме 0 — без заливки и без деления.
export const getHoursBar = ({ worked, norm }: EmployeeHours): HoursBar | null => {
    if (worked === null || norm === null) return null
    if (norm <= 0) return { percent: 0, isLow: false }
    const ratio = worked / norm
    return {
        percent: Math.min(Math.max(ratio, 0), 1) * 100,
        isLow: ratio < LOW_RATIO
    }
}
